<template>
  <v-app class="main-layout">
    <v-layout>
      <v-app-bar flat color="#0A192F">
        <v-app-bar-nav-icon color="white" @click="drawer = !drawer"></v-app-bar-nav-icon>
      </v-app-bar>

      <v-navigation-drawer
        v-model="drawer"
        :location="$vuetify.display.mobile ? 'left' : undefined"
        :temporary="$vuetify.display.mobile"
        :permanent="!$vuetify.display.mobile"
        :rail="rail && !$vuetify.display.mobile"
        class="main-sidebar"
        :width="280"
        :rail-width="72"
        color="#0A192F"
      >
        <div class="sidebar-inner">
          <div v-if="!$vuetify.display.mobile" class="sidebar-header">
            <div class="logo-container">
              <v-avatar :size="rail ? 40 : 48" color="white" class="logo-avatar">
                <v-icon :size="rail ? 24 : 28" color="primary">mdi-school</v-icon>
              </v-avatar>

              <div v-if="!rail" class="logo-text">
                <h2 class="sidebar-title">Panel de Control</h2>
                <p class="sidebar-subtitle">Administración</p>
              </div>
            </div>
          </div>

          <v-divider v-if="!$vuetify.display.mobile" class="sidebar-divider" />

          <v-list nav class="navigation-list">
            <v-list-item
              v-for="item in menuItems"
              :key="item.title"
              :to="item.to"
              class="nav-item"
              :class="{ 'nav-item--active': $route.path === item.to }"
              @click="handleItemClick"
            >
              <template #prepend>
                <v-icon :icon="item.icon" class="nav-icon" />
              </template>

              <v-list-item-title class="nav-title">
                {{ item.title }}
              </v-list-item-title>

              <template #append v-if="item.badge">
                <v-badge :content="item.badge" color="error" inline />
              </template>
            </v-list-item>
          </v-list>

          <div class="sidebar-bottom">
            <v-divider class="sidebar-divider" />
            <v-list nav>
              <v-list-item class="nav-item logout-item" @click="handleLogout">
                <template #prepend>
                  <v-icon icon="mdi-logout" class="nav-icon" />
                </template>
                <v-list-item-title v-if="!rail" class="nav-title">Cerrar Sesión</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
        </div>
      </v-navigation-drawer>

      <v-main class="main-content">
        <div class="content-wrapper">
          <router-view />
        </div>
      </v-main>
    </v-layout>
  </v-app>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useDisplay } from 'vuetify'

const router = useRouter()
const store = useStore()
const { mobile } = useDisplay()

const drawer = ref(!mobile.value)
const rail = ref(false)

watch(mobile, (isMobile) => {
  if (!isMobile) {
    drawer.value = true
    rail.value = false
  }
})

const allMenuItems = ref([
  {
    title: 'Panel de Control',
    icon: 'mdi-view-dashboard',
    to: '/dashboard',
    roles: ['admin', 'teacher'],
  },
  {
    title: 'Gestión de Usuarios',
    icon: 'mdi-account-group',
    to: '/admin/users',
    roles: ['admin'],
  },
  {
    title: 'Gestión de Asignaturas',
    icon: 'mdi-book-open-variant',
    to: '/admin/school-subjects',
    roles: ['admin'],
  },
  {
    title: 'Gestión de grupos',
    icon: 'mdi-account-group',
    to: '/groups',
    roles: ['admin'],
  },
  {
    title: 'Gestión de preguntas',
    icon: 'mdi-ab-testing',
    to: '/questions',
    roles: ['admin', 'teacher'],
  },
  {
    title: 'Gestión de PINs',
    icon: 'mdi-key-variant',
    to: '/pins',
    roles: ['admin', 'teacher'],
  },
  {
    title: 'Gestión de Reportes',
    icon: 'mdi-file-chart',
    to: '/reports',
    roles: ['admin', 'teacher'],
  },
  {
    title: 'Acceso con PIN',
    icon: 'mdi-key',
    to: '/pin-access',
    roles: ['student'],
  },
  {
    title: 'Mi Perfil',
    icon: 'mdi-account',
    to: '/profile',
    roles: ['admin', 'teacher', 'student'],
  },
])

const menuItems = computed(() => {
  const user = store.state.auth.user
  if (!user) return []
  const userRole = user.role
  return allMenuItems.value.filter((item) => item.roles.includes(userRole))
})

const handleItemClick = () => {
  if (mobile.value) {
    drawer.value = false
  }
}

const handleLogout = async () => {
  await store.dispatch('auth/logout')
  router.push('/login')
}
</script>

<style scoped>
/* Estilos existentes */
.main-layout {
  background: #f8fafc;
}

.main-sidebar {
  border-right: 1px solid #e2e8f0 !important;
}

.main-sidebar :deep(.v-navigation-drawer__content) {
  background: linear-gradient(180deg, #0a192f 0%, #112240 100%);
  overflow: visible;
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-bottom: 8px;
}

.sidebar-header {
  padding: 20px 16px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-container-appbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.app-bar-title {
  color: white;
  font-weight: 500;
  font-size: 1.25rem;
}

.logo-avatar {
  flex-shrink: 0;
}

.logo-text {
  min-width: 0;
}

.sidebar-title {
  color: white;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.2;
  margin: 0;
}

.sidebar-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin: 0;
  margin-top: 2px;
}

.sidebar-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
  margin: 0 16px;
}

.user-info-section {
  padding: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  flex-shrink: 0;
}

.user-details {
  min-width: 0;
}

.user-name {
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.2;
}

.user-role {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  margin-top: 2px;
}

.navigation-list {
  flex: 1;
  padding: 8px 16px;
  overflow-y: hidden;
  min-height: 0;
}

.nav-item {
  margin-top: 1rem;
  margin-bottom: 4px;
  color: rgba(255, 255, 255, 0.8) !important;
  border-radius: 12px !important;
  transition: all 0.2s ease;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
}

.nav-item--active {
  background-color: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
}

.nav-item--active :deep(.nav-icon),
.nav-item--active :deep(.nav-title) {
  color: white !important;
}

.nav-icon {
  color: rgba(255, 255, 255, 0.8);
  transition: color 0.2s ease;
}

.nav-title {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  transition: color 0.2s ease;
}

.nav-item:hover .nav-icon,
.nav-item:hover .nav-title {
  color: white;
}

.sidebar-bottom {
  margin-top: auto;
  padding: 16px;
}

.logout-item:hover {
  background-color: rgba(239, 68, 68, 0.2) !important;
}

.toggle-btn {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.toggle-btn:hover {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.main-content {
  background: #f8fafc;
  height: 100vh;
  overflow-y: auto;
}

.content-wrapper {
  min-height: 100%;
  padding: 0;
}

@media (max-width: 960px) {
  .navigation-list {
    padding: 8px 16px;
    overflow-y: auto;
  }
}

.main-sidebar.v-navigation-drawer--rail .sidebar-header,
.main-sidebar.v-navigation-drawer--rail .user-info-section {
  padding-left: 16px;
  padding-right: 16px;
}

.main-sidebar.v-navigation-drawer--rail .navigation-list {
  padding: 8px 12px;
}

.main-sidebar.v-navigation-drawer--rail .nav-item {
  justify-content: center;
}

.main-sidebar.v-navigation-drawer--rail .sidebar-bottom {
  padding-bottom: 60px;
}

@media (min-width: 1024px) and (max-width: 1440px) {
  .main-sidebar :deep(.v-navigation-drawer__content) {
    overflow-y: auto !important;
    overflow-x: hidden !important;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
    scroll-behavior: smooth;
  }

  .main-sidebar :deep(.v-navigation-drawer__content)::-webkit-scrollbar {
    width: 8px;
  }

  .main-sidebar :deep(.v-navigation-drawer__content)::-webkit-scrollbar-track {
    background: transparent;
  }

  .main-sidebar :deep(.v-navigation-drawer__content)::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15));
    border-radius: 6px;
    transition: background 0.3s ease;
  }

  .main-sidebar :deep(.v-navigation-drawer__content)::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0.3));
  }

  .navigation-list {
    flex: 1;
    overflow-y: auto !important;
  }
}
</style>
